// CLIENT PORTAL — the delivery-diagnostics band. PURE (no I/O, no clock).
//
// The five figures that describe HOW THE ADS WERE DELIVERED — impressions,
// clicks, click-through rate, cost per 1,000, cost per click — plus the
// twelve-month sparkline that sits under each one.
//
// ⚠️⚠️ THESE ARE NOT AN OUTCOME, AND THE BAND SAYS SO IN WORDS BEFORE THE
// NUMBERS. Impressions are the classic vanity metric: large, flattering, and
// unrelated to whether anybody enquired. The framing sentence is not decoration
// — it is the one thing stopping a big number reading as a good result, and it
// is asserted by test for that reason.
//
// ⚠️⚠️ THE FIGURE AND ITS SPARKLINE COME FROM DIFFERENT SCOPES, DELIBERATELY,
// AND THE REPORT'S OWN RULE IS WHY. `assembleBlock` narrows the SERIES to the
// dominant currency while `totals` stays all-currency, under a rule stated at
// that call site: *"the charts may narrow to one currency, the headline figures
// never do."* So:
//
//   · The FIGURES are computed here from the all-currency campaign rows. The
//     two counts have no currency at all, and the money ratios are reported PER
//     CURRENCY rather than blended — which also fixes a latent misstatement in
//     the disclosure this band replaces, where every currency's spend was summed
//     and then labelled with the FIRST campaign's currency symbol. Promoting
//     that into a prominent band with a chart under it was not an option.
//
//   · The SPARKLINES come from the series, i.e. the dominant currency — a chart,
//     narrowing, exactly as the rule permits.
//
// ⚠️ On the overwhelmingly common single-currency scope the two are identical
// and none of this is visible. It matters for a group owner running clubs on
// both sides of the Tasman, which is a real customer.
//
// ⚠️ EVERY RATIO IS RE-DERIVED FROM SUMMED NUMERATOR ÷ SUMMED DENOMINATOR, per
// bucket and per currency — never an average of other ratios. Same rule
// `deriveCostPerLead` follows, and it is not a nicety: the mean of daily CTRs
// weights a day with nine impressions the same as a day with ninety thousand.
//
// ⚠️⚠️ A RATIO OF NOTHING IS NULL, NEVER ZERO. A month that served no
// impressions has no click-through rate — "0.00%" would state that we showed the
// ads and nobody clicked, when we showed nothing. Same reasoning that keeps a
// $0 cost per lead off this report.

import type { PortalSeriesPoint } from '../../types/portalReport';

export type DeliveryMetricKey =
  | 'impressions'
  | 'clicks'
  | 'ctr'
  | 'costPerThousand'
  | 'costPerClick';

/**
 * ⚠️ TWO GROUPS, AND THEY ARE NOT INTERCHANGEABLE. The counts are what happened;
 * the ratios are what it cost or converted at. Laying all five in one flat row
 * invites a reader to compare a 1.4 million against a 1.2% — see
 * `deliveryGroups` for why the layout keeps them apart.
 */
export type DeliveryGroup = 'counts' | 'ratios';

export interface DeliveryMetric {
  key: DeliveryMetricKey;
  label: string;
  group: DeliveryGroup;
  /** How the FIGURE is written. `money` figures are reported per currency. */
  kind: 'count' | 'percent' | 'money';
}

export const DELIVERY_METRICS: readonly DeliveryMetric[] = [
  { key: 'impressions', label: 'Impressions', group: 'counts', kind: 'count' },
  { key: 'clicks', label: 'Clicks', group: 'counts', kind: 'count' },
  { key: 'ctr', label: 'Click-through rate', group: 'ratios', kind: 'percent' },
  { key: 'costPerThousand', label: 'Cost per 1,000', group: 'ratios', kind: 'money' },
  { key: 'costPerClick', label: 'Cost per click', group: 'ratios', kind: 'money' },
];

/** The metrics of one group, in display order. */
export function deliveryGroups(): Array<{ group: DeliveryGroup; metrics: DeliveryMetric[] }> {
  return [
    { group: 'counts', metrics: DELIVERY_METRICS.filter((m) => m.group === 'counts') },
    { group: 'ratios', metrics: DELIVERY_METRICS.filter((m) => m.group === 'ratios') },
  ];
}

// ---------------------------------------------------------------------------
// The figures
// ---------------------------------------------------------------------------

/** What the band needs off a campaign row — Meta's and Google's shapes both fit. */
export interface DeliveryCampaignLike {
  spend: number;
  currency: string;
  impressions: number | null;
  clicks: number | null;
}

/** One currency's money ratios. Null where the denominator is zero. */
export interface DeliveryCurrencySlice {
  currency: string;
  spend: number;
  impressions: number;
  clicks: number;
  costPerThousand: number | null;
  costPerClick: number | null;
}

export interface DeliveryFigures {
  /** All-currency. A count has no currency, so blending them is simply addition. */
  impressions: number;
  clicks: number;
  /** Percent, 0-100. Currency-free, so all-currency too. Null with no impressions. */
  ctr: number | null;
  /** ⚠️ PER CURRENCY, never blended. Sorted by code so the order is stable. */
  byCurrency: DeliveryCurrencySlice[];
  /**
   * ⚠️ FALSE means campaigns ran and recorded NO delivery at all — a real state
   * with an explanation, distinct from "there were no campaigns", which the
   * caller handles by omitting the band entirely. Zero impressions AND zero
   * clicks is the only case; either one alone is delivery.
   */
  hasDelivery: boolean;
}

export function deliveryFigures(
  campaigns: readonly DeliveryCampaignLike[],
): DeliveryFigures {
  let impressions = 0;
  let clicks = 0;
  const byCurrency = new Map<string, { spend: number; impressions: number; clicks: number }>();

  for (const c of campaigns) {
    // ⚠️ A null count on a campaign row means the platform reported none for it.
    // Treating it as 0 in a SUM is correct — it contributes nothing — and is not
    // the same as the null-is-a-gap rule, which is about a whole BUCKET nobody
    // measured. There is no bucket here; there is a row that reported nothing.
    const ci = c.impressions ?? 0;
    const cc = c.clicks ?? 0;
    impressions += ci;
    clicks += cc;
    const slice = byCurrency.get(c.currency) ?? { spend: 0, impressions: 0, clicks: 0 };
    slice.spend += c.spend;
    slice.impressions += ci;
    slice.clicks += cc;
    byCurrency.set(c.currency, slice);
  }

  return {
    impressions,
    clicks,
    ctr: ratePercent(clicks, impressions),
    byCurrency: [...byCurrency.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([currency, s]) => ({
        currency,
        spend: s.spend,
        impressions: s.impressions,
        clicks: s.clicks,
        costPerThousand: perThousand(s.spend, s.impressions),
        costPerClick: perUnit(s.spend, s.clicks),
      })),
    hasDelivery: impressions > 0 || clicks > 0,
  };
}

// ---------------------------------------------------------------------------
// The sparklines
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ BELOW THIS MANY MEASURED POINTS THERE IS NO CHART — the FIGURE still
 * shows, and it stays complete and unqualified.
 *
 * Two points are a slope, and a slope drawn as a line invites a reader to read a
 * direction out of what is really one comparison. Three is the fewest that can
 * show a shape rather than a verdict.
 *
 * ⚠️⚠️ AND THE ABSENCE IS NOW EXPLAINED, WHICH REVERSES THE RULE THAT SHIPPED
 * WITH THE SPARKLINES ON 2026-09-01. It was right then: five silent gaps under
 * five small marks were unremarkable, and five lines of apology would have been
 * worse. It is wrong now that each metric owns a full chart CARD — a card with a
 * figure at the top and a blank space beneath it reads as broken, where a
 * missing sparkline read as nothing at all. Same fact, different size, opposite
 * answer; `deliveryHistoryWithheld` is the sentence.
 */
export const MIN_CHART_MONTHS = 3;

/**
 * ONE bucket's value for a delivery metric.
 *
 * ⚠️⚠️ THE ONE PLACE A DELIVERY RATIO IS DERIVED. `chartPlan.valueAt` — which
 * every chart reads through — delegates here rather than repeating the
 * arithmetic, so the rule and the reasoning below cannot come apart from what a
 * chart actually plots.
 *
 * ⚠️ NULL IS A BREAK IN THE LINE, and it means one of two things: the month was
 * never measured, or the ratio is undefined because its denominator was zero.
 * Both are "we cannot draw a point here", and neither invents a number.
 *
 * ⚠️ The COUNT metrics still distinguish the two, which is where the
 * distinction can be made and matters: a measured month that served nothing
 * plots at zero on the impressions chart, while an unmeasured one breaks it.
 */
export function deliveryValueAt(
  point: PortalSeriesPoint,
  key: DeliveryMetricKey,
): number | null {
  switch (key) {
    case 'impressions':
      return point.impressions;
    case 'clicks':
      return point.clicks;
    case 'ctr':
      return point.clicks === null || point.impressions === null
        ? null
        : ratePercent(point.clicks, point.impressions);
    case 'costPerThousand':
      return point.spend === null || point.impressions === null
        ? null
        : perThousand(point.spend, point.impressions);
    case 'costPerClick':
      return point.spend === null || point.clicks === null
        ? null
        : perUnit(point.spend, point.clicks);
  }
}

/** One value per bucket, for the whole run. */
export function deliverySeriesValues(
  series: readonly PortalSeriesPoint[],
  key: DeliveryMetricKey,
): Array<number | null> {
  return series.map((p) => deliveryValueAt(p, key));
}

/** Enough measured points to be worth charting? See `MIN_CHART_MONTHS`. */
export function hasEnoughHistory(values: ReadonlyArray<number | null>): boolean {
  return values.filter((v) => v !== null).length >= MIN_CHART_MONTHS;
}

// ---------------------------------------------------------------------------

/**
 * ⚠️ Every one of these returns NULL rather than 0 on a zero denominator, and
 * that is the same decision in three places: a rate of nothing is undefined, not
 * a measured zero.
 */
function ratePercent(numerator: number, denominator: number): number | null {
  return denominator > 0 ? (numerator / denominator) * 100 : null;
}

function perThousand(spend: number, impressions: number): number | null {
  return impressions > 0 ? (spend / impressions) * 1000 : null;
}

function perUnit(spend: number, units: number): number | null {
  return units > 0 ? spend / units : null;
}
